import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Clock, User, Bot, Send, Ticket, CheckCircle } from "lucide-react";
import { useAppDispatch } from "@/store/hooks";
import { replyToTicket } from "@/store/slices/ticketSlice";
import { useAuth } from "@/context/Auth";

export default function TicketDetails({ ticket, onReply, onDelete }) {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSubmitting(true);
    try {
      await dispatch(replyToTicket({
        ticketId: ticket.ticket_id || ticket.id,
        message: replyContent.trim(),
      })).unwrap();
      setReplyContent("");
      // Call parent callback to refresh data
      if (onReply) onReply();
    } catch (error) {
      console.error("Failed to submit reply:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Flatten nested messages for display
  const flattenMessages = (messages, level = 0) => {
    const result = [];
    messages?.forEach((message) => {
      result.push({ ...message, level });
      if (message.children && message.children.length > 0) {
        result.push(...flattenMessages(message.children, level + 1));
      }
    });
    return result;
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "Open":
        return "bg-green-100 text-green-700";
      case "In Progress":
        return "bg-blue-100 text-blue-700";
      case "Pending":
        return "bg-yellow-100 text-yellow-700";
      case "Closed":
        return "bg-gray-100 text-gray-700";
      case "Resolved":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const getSenderName = (message) => {
    // For user messages, always show "You" since users can only see their own tickets
    if (message.sender_type === "user") {
      return "You";
    }
    // For admin messages, show sender name or "Support Agent"
    if (message.sender_type === "admin") {
      return message.sender_name || "Support Agent";
    }
    // Fallback
    return message.sender_name || "Support Agent";
  };

  const isUserMessage = (message) => {
    // Check if the message is from the current user
    if (message.sender_type === "user") {
      return true;
    }
    // If sender_type is admin, it's from support
    if (message.sender_type === "admin") {
      return false;
    }
    // Fallback: check if sender_name matches current user
    if (user && message.sender_name) {
      const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim().toLowerCase();
      const senderName = message.sender_name.trim().toLowerCase();
      if (userName && senderName && userName === senderName) {
        return true;
      }
    }
    // Also check against ticket creator
    if (ticket.createdBy && message.sender_name) {
      const creatorName = `${ticket.createdBy.firstName || ""} ${ticket.createdBy.lastName || ""}`.trim().toLowerCase();
      const senderName = message.sender_name.trim().toLowerCase();
      if (creatorName && senderName && creatorName === senderName) {
        return true;
      }
    }
    return false; // Default to agent/support
  };

  // Get all messages: initial description + messages from API
  const allMessages = [];
  
  // Add initial ticket description as first message (if not already in messages)
  // The backend creates an initial message, but we'll check if it exists
  const hasInitialMessage = ticket.messages && ticket.messages.length > 0;
  
  if (!hasInitialMessage && ticket.description) {
    // Fallback: if no messages exist, show the description
    allMessages.push({
      id: "initial",
      message: ticket.description,
      created_at: ticket.created_at,
      sender: ticket.createdBy
        ? `${ticket.createdBy.firstName || ""} ${ticket.createdBy.lastName || ""}`.trim() || "You"
        : "You",
      isUser: true,
      level: 0,
    });
  }

  // Add flattened messages from API
  const flattenedMessages = flattenMessages(ticket.messages || []);
  flattenedMessages.forEach((msg) => {
    allMessages.push({
      id: msg.id,
      message: msg.message,
      created_at: msg.created_at,
      sender: getSenderName(msg),
      isUser: isUserMessage(msg),
      level: msg.level || 0,
    });
  });

  const ticketId = ticket.ticket_id || ticket.id;

  return (
    <div className="flex flex-col h-full w-full max-w-full sm:max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-4 border-b">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold text-primary flex items-center gap-2 flex-wrap break-words">
            <Ticket className="w-5 h-5 flex-shrink-0" />
            <span className="break-words">{ticket.subject}</span>
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words whitespace-normal">
            Ticket ID: {ticketId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            className={`${getStatusBadgeColor(ticket.status)} text-xs sm:text-sm flex-shrink-0`}
          >
            {ticket.status}
          </Badge>
          {ticket.priority && (
            <Badge className="bg-purple-100 text-purple-700 text-xs sm:text-sm">
              {ticket.priority}
            </Badge>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-6">
        {allMessages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No messages yet.</p>
          </div>
        ) : (
          allMessages.map((message, index) => (
            <div
              key={message.id || index}
              className="flex items-start gap-3 flex-wrap sm:flex-nowrap"
              style={{ marginLeft: `${(message.level || 0) * 24}px` }}
            >
              <div className="flex-shrink-0">
                {message.isUser ? (
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
                ) : (
                  <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0 max-w-full">
                <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <span className="font-semibold text-primary break-words">{message.sender}</span>
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="break-words">{formatDateTime(message.created_at)}</span>
                </div>
                <div className="mt-1 p-3 rounded-lg bg-gray-50 text-gray-800 break-words whitespace-pre-wrap max-w-full">
                  <p className="break-words">{message.message}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reply Form */}
      {ticket.status !== "Closed" && ticket.status !== "Resolved" && (
        <form onSubmit={handleReplySubmit} className="pt-4 border-t w-full">
          <Label htmlFor="reply" className="text-sm sm:text-base">
            Reply to Ticket
          </Label>
          <div className="mt-1 flex flex-col sm:flex-row gap-2 w-full">
            <Textarea
              id="reply"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="Type your reply here..."
              rows={3}
              className="flex-1 min-w-0 resize-y"
              disabled={submitting}
            />
            <Button
              type="submit"
              disabled={submitting || !replyContent.trim()}
              className="bg-primary hover:bg-primary/90 text-white flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base flex-shrink-0 w-full sm:w-auto justify-center"
            >
              <Send className="w-4 h-4" /> {submitting ? "Sending..." : "Send Reply"}
            </Button>
          </div>
        </form>
      )}

      {/* Closed/Resolved Message */}
      {(ticket.status === "Closed" || ticket.status === "Resolved") && (
        <div className="pt-4 border-t text-center text-gray-600">
          <p className="flex flex-wrap items-center justify-center gap-2 text-sm sm:text-base">
            <CheckCircle className="w-5 h-5 text-green-500" />
            This ticket is {ticket.status.toLowerCase()}.
          </p>
        </div>
      )}
    </div>
  );
}
